import { Draggable, Droppable } from '@hello-pangea/dnd';
import { MoreHoriz } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Button } from '@/components/Button';
import { IS_V2_WRITE_ENABLED } from '@/configs/v2Write';
import { Board, Sprint } from '@/tracker/types';

import { BoardCard } from '../BoardCard';
import { BacklogColumn as Column } from '../../model/BacklogStore';

import { formatDates } from './formatDates';

import s from './BacklogColumn.module.scss';

type Props = {
  column: Column;
  estimateBy: Board['estimateBy'];
  canManage: boolean;
  hasActiveSprint: boolean;
  isSaving: boolean;
  onStart: (sprint: Sprint) => void;
  onClose: (sprint: Sprint) => void;
  onEdit: (sprint: Sprint) => void;
  onDelete: (sprint: Sprint) => void;
};

/** Колонка бэклога или спринта: шапка с действиями и задачи, которые можно перетаскивать */
const BacklogColumn: React.FC<Props> = observer(
  ({
    column,
    estimateBy,
    canManage,
    hasActiveSprint,
    isSaving,
    onStart,
    onClose,
    onEdit,
    onDelete,
  }) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const { sprint, tasks } = column;
    const isDraft = sprint?.status === 'draft';

    return (
      <section className={cn(s.column, sprint?.status === 'active' && s.column_active)}>
        <header className={s.column__header}>
          <div className={s.column__title}>
            <span className={s.column__name}>{sprint?.name ?? 'Бэклог'}</span>
            <span className={s.column__count}>{tasks.length}</span>
          </div>
          {sprint && (
            <div className={s.column__meta}>
              {formatDates(sprint)}
              {sprint.status === 'active' && <span className={s.column__badge}>идёт</span>}
            </div>
          )}

          {sprint && canManage && (
            <div className={s.column__actions}>
              {isDraft && (
                <Tooltip title={hasActiveSprint ? 'На доске уже идёт спринт' : ''}>
                  {/* span: Tooltip не видит события выключенной кнопки */}
                  <span>
                    <Button
                      variant="text"
                      size="small"
                      disabled={hasActiveSprint || isSaving}
                      onClick={() => onStart(sprint)}
                    >
                      Запустить
                    </Button>
                  </span>
                </Tooltip>
              )}
              {sprint.status === 'active' && (
                <Button
                  variant="text"
                  size="small"
                  disabled={isSaving}
                  onClick={() => onClose(sprint)}
                >
                  Завершить
                </Button>
              )}
              <IconButton
                size="small"
                aria-label={`Действия со спринтом ${sprint.name}`}
                onClick={(event) => setMenuAnchor(event.currentTarget)}
              >
                <MoreHoriz fontSize="small" />
              </IconButton>
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    onEdit(sprint);
                  }}
                >
                  Изменить
                </MenuItem>
                {/* Удалить можно только черновик: идущий или закрытый спринт завершают */}
                {isDraft && (
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      onDelete(sprint);
                    }}
                    sx={{ color: 'var(--danger-color)' }}
                  >
                    Удалить
                  </MenuItem>
                )}
              </Menu>
            </div>
          )}
        </header>

        <Droppable droppableId={column.id}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={cn(s.column__body, snapshot.isDraggingOver && s.column__body_over)}
            >
              {tasks.map((task, index) => (
                <Draggable
                  key={task.key}
                  draggableId={task.key}
                  index={index}
                  isDragDisabled={!IS_V2_WRITE_ENABLED}
                >
                  {(draggable) => (
                    <div
                      ref={draggable.innerRef}
                      {...draggable.draggableProps}
                      {...draggable.dragHandleProps}
                      className={s.column__card}
                    >
                      <BoardCard issue={task} estimateBy={estimateBy} />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              {!tasks.length && (
                <div className={s.column__empty}>
                  {sprint ? 'Перетащите задачи из бэклога' : 'Задач без спринта нет'}
                </div>
              )}
            </div>
          )}
        </Droppable>
      </section>
    );
  },
);

export { BacklogColumn };
